/* eslint-disable react/prop-types */
/* eslint-disable react/require-default-props */
import React, {
  useContext, useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { PRODUCT_POD_V7_POD_KEY_CLICK } from '../../constants';
import PianoTabContext from '../../context/PianoContext';

const CustomTabList = ({ children, ariaLabel }) => {
  return (
    <div
      aria-label={ariaLabel}
      className="sui-col-span-2 sui-flex sui-justify-evenly sui-items-center"
      role="tablist"
    >
      {children}
    </div>
  );
};

const CustomTab = ({
  label = '', value = 0, currentValue = 0, onChange = () => {}, itemId, ...props
}) => {
  const isSelected = value === currentValue;
  const fontStyle = isSelected ? 'sui-font-bold' : 'sui-font-normal sui-text-subtle';
  const ariaSelected = isSelected ? 'true' : 'false';

  const buttonStyles = classNames(
    'sui-tab-base focus-visible:sui-bg-focus focus-visible:sui-text-primary',
    'enabled:hover:sui-bg-subtle enabled:hover:sui-text-primary',
    fontStyle,
    'sui-text-sm sui-leading-tight sui-tracking-normal sui-normal-case',
    'sui-line-clamp-unset sui-text-primary'
  );

  const tabData = {
    podKey: label.toLowerCase(),
    podAnchorSku: itemId,
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger(PRODUCT_POD_V7_POD_KEY_CLICK, tabData);
    }
    setTimeout(() => {
      onChange(null, value);
    }, 100);
  };

  return (
    <button
      className={buttonStyles}
      /* Override default min-height, height and padding from sui-tab-base Stencil class */
      style={{ minHeight: '18px', height: '18px', padding: '0px' }}
      type="button"
      role="tab"
      aria-selected={ariaSelected}
      id={`sui-${label}-Tab`}
      aria-controls={`sui-${label}-Panel`}
      onClick={(event) => handleClick(event)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {label}
    </button>
  );
};

const CustomPanel = ({ children, value, isActive, noPadding = false }) => {
  return (
    <div
      data-testid="pod-panel"
      className={classNames({ 'sui-p-2': !noPadding })}
      style={{
        gridRow: '1',
        gridColumn: '1',
        visibility: isActive ? 'visible' : 'hidden',
        transition: 'opacity 0.5s ease-out',
        opacity: isActive ? 1 : 0
      }}
      role="tabpanel"
      aria-hidden={!isActive}
      id={`pod-key-${value}`}
      aria-labelledby={`pod-key-${value}`}
    >
      {children}
    </div>
  );
};

CustomTab.displayName = 'CustomTab';

CustomTab.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  currentValue: PropTypes.number,
  onChange: PropTypes.func,
  itemId: PropTypes.string.isRequired,
};

// For each TabPanel content
export const PodPanel = ({ children = null, value, noPadding = false }) => {
  const { keyValue: currentValue } = useContext(PianoTabContext);
  const isActive = value === currentValue;

  return (
    <CustomPanel value={value} isActive={isActive} noPadding={noPadding}>
      {children}
    </CustomPanel>
  );
};

PodPanel.displayName = 'PodPanel';

PodPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
};

export const PodKeyboard = ({ children = null, zIndex = null }) => {
  const podKeyboardStyles = classNames(
    'sui-border-solid sui-border-primary',
    'sui-inline-block sui-grid sui-grid-cols-3',
    'sui-mt-auto sui-p-1 sui-w-full',
    'sui-whitespace-nowrap sui-overflow-hidden',
    zIndex && `sui-z-${zIndex}`
  );

  return (
    <div
      data-testid="pod-keyboard"
      className={podKeyboardStyles}
      style={{
        maxHeight: '38px',
        borderTopWidth: '.5px',
        gridRow: '2',
        gridColumn: '1'
      }}
    >
      {children}
    </div>
  );
};

PodKeyboard.displayName = 'PodKeyboard';

PodKeyboard.propTypes = {
  children: PropTypes.node,
  zIndex: PropTypes.number
};

// Pod tabs (key's label): Details | Specs | Images | Reviews
export const PodKeys = ({ ariaLabel = 'pod tabs', keys, itemId }) => {
  const { keyValue: value, onTabChange } = useContext(PianoTabContext);

  useEffect(() => {
    // Ensure the first tab is selected on mount
    if (keys && keys.length > 0) {
      onTabChange(0, keys[0]);
    }
  }, []);

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  if (!keys || keys.length === 0) return null;

  return (
    <CustomTabList
      data-testid="pod-keys"
      ariaLabel={ariaLabel}
    >
      { keys.map((keyLabel, index) => {
        return (
          <CustomTab
            label={keyLabel}
            value={index}
            currentValue={value}
            onChange={() => onTabChange(index, keyLabel)}
            itemId={itemId}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...a11yProps(index)}
          />
        );
      })}
    </CustomTabList>
  );

};

PodKeys.displayName = 'PodKeys';

PodKeys.propTypes = {
  ariaLabel: PropTypes.string,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export const PodActions = ({ children = null }) => {
  return (
    <div data-testid="pod-actions" className="sui-flex sui-items-center sui-justify-end sui-gap-2">
      {children}
    </div>
  );
};

PodActions.displayName = 'PodActions';

PodActions.propTypes = {
  children: PropTypes.node,
};